import axiosInstance from '../../../config/axios';

export type CatalogItemKind = 'SERVICE' | 'MATERIAL' | 'FLAT_FEE' | 'TEXT';

export interface CatalogItem {
  id: number;
  item_number: string;
  name: string;
  description?: string;
  kind: CatalogItemKind;
  unit: string;
  sales_price: string;
  purchase_price?: string | null;
  default_markup_percent?: string | null;
  tax_rate?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  // FE-BILL-HF-R30-4: Scope items count from listing
  scope_items_count?: number;
}

export interface CatalogItemPayload {
  item_number: string;
  name: string;
  description?: string;
  kind: CatalogItemKind;
  unit: string;
  sales_price?: string;
  purchase_price?: string;
  default_markup_percent?: string;
  tax_rate?: string;
  is_active?: boolean;
}

export interface CatalogSearchParams {
  search?: string;
  kind?: CatalogItemKind;
  is_active?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// FE-BILL-R28-1: Catalog Item Scope Items (Subtasks)
export interface CatalogScopeItem {
  id: number;
  catalog_item_id: number;
  text: string;
  sort_order: number;
  is_active: boolean;
}

export interface CatalogScopeItemsResponse {
  catalog_item_id: number;
  scope_items: CatalogScopeItem[];
}

export interface CatalogScopeItemPayload {
  text: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function unwrap<T>(response: { data: T }): T {
  return response.data;
}

export default class CatalogService {
  static async search(params?: CatalogSearchParams): Promise<PaginatedResponse<CatalogItem>> {
    const queryParams: Record<string, string> = {};

    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.kind) {
      queryParams.kind = params.kind;
    }
    if (params?.is_active !== undefined) {
      queryParams.is_active = String(params.is_active);
    }
    if (params?.page) {
      queryParams.page = String(params.page);
    }
    if (params?.page_size) {
      queryParams.page_size = String(params.page_size);
    }
    if (params?.ordering) {
      queryParams.ordering = params.ordering;
    }

    const query = new URLSearchParams(queryParams).toString();
    const url = query ? `/api/admin/catalog-items/?${query}` : '/api/admin/catalog-items/';

    const response = unwrap(await axiosInstance.get<CatalogItem[] | PaginatedResponse<CatalogItem>>(url));

    if (Array.isArray(response)) {
      return {
        count: response.length,
        next: null,
        previous: null,
        results: response,
      };
    }
    return response;
  }

  static async get(id: number): Promise<CatalogItem> {
    return unwrap(await axiosInstance.get<CatalogItem>(`/api/admin/catalog-items/${id}/`));
  }

  static async create(payload: CatalogItemPayload): Promise<CatalogItem> {
    return unwrap(await axiosInstance.post<CatalogItem>('/api/admin/catalog-items/', payload));
  }

  static async update(id: number, payload: Partial<CatalogItemPayload>): Promise<CatalogItem> {
    return unwrap(await axiosInstance.patch<CatalogItem>(`/api/admin/catalog-items/${id}/`, payload));
  }

  static async delete(id: number): Promise<void> {
    await axiosInstance.delete(`/api/admin/catalog-items/${id}/`);
  }

  static getKindLabel(kind: CatalogItemKind): string {
    const labels: Record<CatalogItemKind, string> = {
      SERVICE: 'Service',
      MATERIAL: 'Material',
      FLAT_FEE: 'Flat fee',
      TEXT: 'Text',
    };
    return labels[kind] || kind;
  }

  static getKindOptions(): { value: CatalogItemKind; label: string }[] {
    return [
      { value: 'SERVICE', label: 'Service' },
      { value: 'MATERIAL', label: 'Material' },
      { value: 'FLAT_FEE', label: 'Flat fee' },
      { value: 'TEXT', label: 'Text' },
    ];
  }

  static formatPrice(price: number | string): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(numPrice);
  }

  static formatDecimal(value: number | string | null | undefined, decimals: number = 2): string {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '';
    return num.toFixed(decimals);
  }

  static parseDecimalInput(input: string): number | null {
    if (!input || input.trim() === '') return null;
    const normalized = input.replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? null : num;
  }

  static calculateSalesPrice(purchasePrice: number, markupPercent: number): number {
    return purchasePrice * (1 + markupPercent / 100);
  }

  // ============================================================================
  // FE-BILL-R28-1: Catalog Scope Items (Subtasks) API
  // CANONICAL URLs - NO tolerant parsing
  // ============================================================================

  static async fetchScopeItems(catalogItemId: number): Promise<CatalogScopeItemsResponse> {
    const url = `/api/admin/catalog-items/${catalogItemId}/scope-items/`;
    return unwrap(await axiosInstance.get<CatalogScopeItemsResponse>(url));
  }

  static async createScopeItem(
    catalogItemId: number,
    payload: CatalogScopeItemPayload
  ): Promise<CatalogScopeItem> {
    const url = `/api/admin/catalog-items/${catalogItemId}/scope-items/`;
    return unwrap(await axiosInstance.post<CatalogScopeItem>(url, payload));
  }

  static async updateScopeItem(
    scopeItemId: number,
    payload: Partial<CatalogScopeItemPayload>
  ): Promise<CatalogScopeItem> {
    const url = `/api/admin/catalog-item-scope-items/${scopeItemId}/`;
    return unwrap(await axiosInstance.patch<CatalogScopeItem>(url, payload));
  }

  static async deleteScopeItem(scopeItemId: number): Promise<void> {
    const url = `/api/admin/catalog-item-scope-items/${scopeItemId}/`;
    await axiosInstance.delete(url);
  }
}
